/**
 * Instance-scoped localStorage utilities.
 *
 * Prefixes all storage keys with the Firebase project ID so that settings
 * are unique per application instance (client deployment).
 */

const INSTANCE_PREFIX = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'default';

/**
 * Get the full storage key prefixed with the instance ID.
 */
export function getInstanceStorageKey(key: string): string {
    return `${INSTANCE_PREFIX}:${key}`;
}

/**
 * Save a value to localStorage with instance-scoped key.
 */
export function saveToInstanceStorage<T>(key: string, value: T): void {
    try {
        localStorage.setItem(getInstanceStorageKey(key), JSON.stringify(value));
    } catch (error) {
        console.warn('Failed to save to localStorage:', error);
    }
}

/**
 * Load a value from localStorage with instance-scoped key.
 */
export function loadFromInstanceStorage<T>(key: string, defaultValue: T): T {
    try {
        const stored = localStorage.getItem(getInstanceStorageKey(key));
        return stored ? JSON.parse(stored) : defaultValue;
    } catch (error) {
        console.warn('Failed to load from localStorage:', error);
        return defaultValue;
    }
}

/**
 * Remove a value from localStorage with instance-scoped key.
 */
export function removeFromInstanceStorage(key: string): void {
    try {
        localStorage.removeItem(getInstanceStorageKey(key));
    } catch (error) {
        console.warn('Failed to remove from localStorage:', error);
    }
}
